export const CMS_RESPONSE_BYTE_LIMIT = 1_000_000;
export const CMS_FETCH_TIMEOUT_MS = 5_000;
export const CMS_MAX_RECORDS = 100;
export const CMS_RICH_TEXT_MAX_DEPTH = 20;
export const CMS_RICH_TEXT_MAX_NODES = 2_000;

export class ResponseTooLargeError extends Error {
  constructor() {
    super('Upstream response exceeded the permitted byte budget.');
    this.name = 'ResponseTooLargeError';
  }
}

export async function readJsonResponseBounded(
  response: Response,
  limit = CMS_RESPONSE_BYTE_LIMIT
): Promise<unknown> {
  const declared = response.headers?.get?.('content-length');
  if (declared && /^\d+$/.test(declared) && Number(declared) > limit) {
    await response.body?.cancel().catch(() => undefined);
    throw new ResponseTooLargeError();
  }
  if (!response.body) return response.json(); // Test/polyfill compatibility only.

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > limit) {
        void reader.cancel().catch(() => undefined);
        throw new ResponseTooLargeError();
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return JSON.parse(new TextDecoder().decode(bytes));
}

/** Bounds arrays and rich-text-like child graphs before they reach recursive renderers. */
export function validateCmsPayload(value: unknown): unknown {
  let nodes = 0;
  const visit = (current: unknown, depth: number): void => {
    if (depth > CMS_RICH_TEXT_MAX_DEPTH || ++nodes > CMS_RICH_TEXT_MAX_NODES) {
      throw new Error('CMS payload structure exceeds safety limits.');
    }
    if (Array.isArray(current)) {
      if (current.length > CMS_MAX_RECORDS) throw new Error('CMS array exceeds safety limits.');
      current.forEach((item) => visit(item, depth + 1));
    } else if (current && typeof current === 'object') {
      Object.values(current as Record<string, unknown>).forEach((item) => visit(item, depth + 1));
    }
  };
  visit(value, 0);
  return value;
}
